// Client-side photo pipeline:
//   1. validateInputFile — content-type allowlist + size cap + HEIC-safari gate
//   2. resizeImage — HTMLCanvasElement.drawImage → toBlob('image/webp', 0.85)
//   3. uploadPair — parallel PUT to two presigned URLs
//
// EXIF strip is implicit: canvas redraw drops all metadata. We never re-emit it.


export const ALLOWED_INPUT_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
] as const);

export const MAX_INPUT_BYTES = 25 * 1024 * 1024; // 25 MB

export const TARGET_DIMENSIONS = {
  thumb: { maxEdge: 400 },
  full: { maxEdge: 1920 },
  // Photo import (Slice D): high-res copy sent to the vision LLM. Only produced
  // when explicitly requested (see resizeImage), so recipe/step photos don't pay
  // for a 2560px encode they never upload.
  extract: { maxEdge: 2560 },
} as const;

export const WEBP_QUALITY = 0.85;

export type ValidationResult =
  | { ok: true }
  | { ok: false; code: "TOO_LARGE" | "UNSUPPORTED_TYPE" | "HEIC_NON_SAFARI"; message: string };

export function validateInputFile(
  file: File,
  opts: { isSafari: boolean },
): ValidationResult {
  if (file.size > MAX_INPUT_BYTES) {
    return {
      ok: false,
      code: "TOO_LARGE",
      message: `File is ${(file.size / 1024 / 1024).toFixed(1)} MB; max is 25 MB`,
    };
  }
  if (!ALLOWED_INPUT_TYPES.has(file.type as typeof ALLOWED_INPUT_TYPES extends Set<infer T> ? T : never)) {
    return {
      ok: false,
      code: "UNSUPPORTED_TYPE",
      message: `Unsupported type ${file.type}. Use JPEG, PNG, WebP, or HEIC.`,
    };
  }
  if (file.type === "image/heic" && !opts.isSafari) {
    return {
      ok: false,
      code: "HEIC_NON_SAFARI",
      message: "HEIC isn't supported on this device. Try JPEG/PNG/WebP, or upload from iPhone Safari.",
    };
  }
  return { ok: true };
}

export function isSafari(): boolean {
  const ua = navigator.userAgent;
  return ua.includes("Safari") && !ua.includes("Chrome") && !ua.includes("Chromium");
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      const msg = typeof e === "string" ? e : "image load failed";
      reject(new Error(`Failed to decode image: ${msg}`));
    };
    img.src = url;
  });
}

function scaleDimensions(
  src: { width: number; height: number },
  maxEdge: number,
): { width: number; height: number } {
  const longer = Math.max(src.width, src.height);
  if (longer <= maxEdge) return { width: src.width, height: src.height };
  const scale = maxEdge / longer;
  return {
    width: Math.round(src.width * scale),
    height: Math.round(src.height * scale),
  };
}

function canvasToWebp(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("canvas.toBlob returned null"))),
      "image/webp",
      quality,
    );
  });
}

// thumb/full always present; `extract` present only when requested (photo import).
export type ResizeResult = { thumb: Blob; full: Blob; extract?: Blob };

const DEFAULT_RESIZE_SIZES: ReadonlyArray<keyof typeof TARGET_DIMENSIONS> = ["thumb", "full"];

export async function resizeImage(
  file: File,
  sizes: ReadonlyArray<keyof typeof TARGET_DIMENSIONS> = DEFAULT_RESIZE_SIZES,
): Promise<ResizeResult> {
  const img = await loadImage(file);
  const blobs: Partial<ResizeResult> = {};
  for (const size of sizes) {
    const { width, height } = scaleDimensions(
      { width: img.width, height: img.height },
      TARGET_DIMENSIONS[size].maxEdge,
    );
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("canvas 2d context unavailable");
    ctx.drawImage(img, 0, 0, width, height);
    blobs[size] = await canvasToWebp(canvas, WEBP_QUALITY);
  }
  return blobs as ResizeResult;
}

export type PresignedUrls = { thumbUrl: string; fullUrl: string };

export async function uploadPair(
  blobs: ResizeResult,
  urls: PresignedUrls,
  opts?: { onProgress?: (size: "thumb" | "full", phase: "start" | "done") => void },
): Promise<void> {
  const put = async (url: string, blob: Blob, size: "thumb" | "full"): Promise<void> => {
    opts?.onProgress?.(size, "start");
    const res = await fetch(url, {
      method: "PUT",
      headers: { "Content-Type": "image/webp" },
      body: blob,
    });
    if (!res.ok) {
      throw new Error(`upload ${size} failed: ${res.status}`);
    }
    opts?.onProgress?.(size, "done");
  };
  await Promise.all([
    put(urls.thumbUrl, blobs.thumb, "thumb"),
    put(urls.fullUrl, blobs.full, "full"),
  ]);
}

export type ImportPresignedUrls = { thumbUrl: string; fullUrl: string; extractUrl: string };

/**
 * Photo import (Slice D): PUT thumb + full + the high-res `extract` copy to their
 * three presigned URLs in parallel. Mirrors uploadPair with a third object.
 */
export async function uploadTriple(
  blobs: { thumb: Blob; full: Blob; extract: Blob },
  urls: ImportPresignedUrls,
  opts?: { onProgress?: (size: "thumb" | "full" | "extract", phase: "start" | "done") => void },
): Promise<void> {
  const put = async (url: string, blob: Blob, size: "thumb" | "full" | "extract"): Promise<void> => {
    opts?.onProgress?.(size, "start");
    const res = await fetch(url, {
      method: "PUT",
      headers: { "Content-Type": "image/webp" },
      body: blob,
    });
    if (!res.ok) {
      throw new Error(`upload ${size} failed: ${res.status}`);
    }
    opts?.onProgress?.(size, "done");
  };
  await Promise.all([
    put(urls.thumbUrl, blobs.thumb, "thumb"),
    put(urls.fullUrl, blobs.full, "full"),
    put(urls.extractUrl, blobs.extract, "extract"),
  ]);
}
